import { Observable } from 'rxjs';
import { tap } from 'rxjs';

import { BasicInterfaceTypeEnum, CatsNoBillCodeDescriptionEnum } from '@shared-enums/cats.enum';
import { arrayIsTruthy, findUnique, syncObjectProperties } from '@shared-helpers/array-object-helpers';
import { convertSapDateToMoment, convertSapDateToWeekDayString, getDayString } from '@shared-helpers/date-helpers';
import { addDecimals, safeCompare, toTitleCase } from '@shared-helpers/string-number-helpers';
import { IConditionToDefault, IHttpResponseEquipment } from '@shared-interfaces/api';
import { IDayOfWeek, IWeekDayNumbers } from '@shared-interfaces/dates';

import { ICatRecord } from '../interfaces/incoming';
import { IEquipmentBillingSummaryRow, IEquipmentPayrollSummaryRow, ITableConfigEntity } from '../interfaces/internal-interfaces';
import { DatahubService } from '../services/datahub.service';
import {
  BasicInterface,
  IBasicInterfaceContext,
  IBasicInterfaceContract,
  IBasicInterfaceNotProductionTextContract,
} from './basicInterface';
import { IOutgoingCatsArrays } from './catsDB';
import { ContractHandler } from './contracts/contract-handler';
import { EquipmentCatsRecord } from './EquipmentCatsRecord';
import { makeBillingEquipmentDailyConfig } from './table-configs/billing-tables.config';
import { makePayrollEquipmentDailyConfig } from './table-configs/payroll-tables.config';

export class Equipment extends BasicInterface implements IBasicInterfaceContract, IBasicInterfaceNotProductionTextContract {
  readonly billCodeType = BasicInterfaceTypeEnum.Equipment;

  //calculated and tracked properties
  payrollGrid: IEquipmentPayrollSummaryRow[] = []; //this array gives values to the summary table for the employee payroll view.
  billingGrid: IEquipmentBillingSummaryRow[] = [];
  detailPayneRecords: EquipmentCatsRecord[] = [];

  display: string;
  tooltip: string;

  payrollTotal = '0';
  billingTotal = '0';
  defaultBillCode: string = CatsNoBillCodeDescriptionEnum.Unassigned;

  //values straight from api.
  EquipmentId: string;
  Description: string;
  Type: string;
  TypeDescription: string;
  CostCenter: string;
  ServiceLine: string;
  ValidFrom: string;
  ValidTo: string;
  CCWeek: string;
  InvalidDates: string;

  constructor(
    equipment: IHttpResponseEquipment,
    public datahub: DatahubService,
    context: IBasicInterfaceContext,
  ) {
    super(context);
    syncObjectProperties(equipment, this, false);
    this.setDisplayProperty();
    // this.handleCatsRecordsSet()
  }

  public isInvalid(day: IWeekDayNumbers): boolean {
    return this.InvalidDates.includes(String(day));
  }

  setDisplayProperty() {
    this.display = toTitleCase(`${this.EquipmentId} - ${this.Description}`);
    this.tooltip = this.Description;
  }

  setCatsRecords(catsRecords: ICatRecord[]): void {
    this.catsRecords = catsRecords;
    this.visibleCatsRecords = catsRecords.filter((x) => Number(x.Catshours) > 0);

    this.handleCatsRecordsSet();
  }

  handleCatsRecordsSet() {
    //once cats records are set, we should recalculate totals and re-render the object in the view. Keeping this to employee level should allow us to refresh just this entity when records are created destroyed or modified.
    if (this.isPayroll || this.payrollGrid.length) this.calculatePayrollGrid();
    else this.calculateBillingGrid(this.filteredTimesheet);
    this.checkApprovalStatus(this.catsRecords);
  }

  addCatsRecords(addedRecords: ICatRecord[]) {
    this.setCatsRecords(this.catsRecords.concat(addedRecords));
  }

  public filterByTimesheet(timesheetNo: string): void {
    this.show();
    this.shouldNeverShow = false;
    this.filteredTimesheet = '';
    if (timesheetNo) this.filteredTimesheet = timesheetNo;
    this.calculateBillingGrid(this.filteredTimesheet);
    if (this.billingGrid.some((x) => x.total === 0)) this.hide();
  }

  calculatePayrollGrid() {
    const row = this.makeEmptyEquipmentPayrollSummaryRow();
    this.payrollTotal = '0';
    if (arrayIsTruthy(this.visibleCatsRecords)) {
      this.visibleCatsRecords.forEach((record) => {
        const workDateAsMoment = convertSapDateToMoment(record.Workdate);
        const weekDay = getDayString(workDateAsMoment);
        const hours = record.Catshours ?? '0';
        row[weekDay] = addDecimals(hours, row[weekDay]);
        this.payrollTotal = addDecimals(this.payrollTotal, hours);
      });
    } else {
      return (this.payrollGrid = [row]);
    }
    row.total = this.payrollTotal;
    return (this.payrollGrid = [row]);
  }

  makeEmptyEquipmentPayrollSummaryRow(): IEquipmentPayrollSummaryRow {
    //provides the default values for the employee payroll summary view.
    return {
      equipment: this.display,
      total: 0,
      sun: 0,
      mon: 0,
      tue: 0,
      wed: 0,
      thu: 0,
      fri: 0,
      sat: 0,
    };
  }

  public calculateBillingGrid(timesheetNo?: string): void {
    let recordPool = this.visibleCatsRecords;
    this.billingTotal = '0';
    if (timesheetNo) recordPool = recordPool.filter((x) => safeCompare(x.Extdocumentno, timesheetNo));
    if (recordPool.length > 0) {
      const codes = findUnique<string>(recordPool, 'Zmatnr', true);
      this.billingGrid = codes.map((x) => {
        return this.makeBillingRow(x, recordPool);
      });
    } else {
      // This happens when adding equipment in billing
      this.billingGrid = [this.makeEmptyEquipmentBillingSummaryRow(this.defaultBillCode)];
    }
  }

  public makeBillingRow(code: string, recordPool: ICatRecord[]): IEquipmentBillingSummaryRow {
    const row = this.makeEmptyEquipmentBillingSummaryRow(code);
    const records = recordPool.filter((x) => x.Zmatnr === code);
    records.forEach((record) => {
      const workDateAsMoment = convertSapDateToMoment(record.Workdate);
      const weekDay = getDayString(workDateAsMoment);
      const hours = Number(record.Catshours); //can't perform operations over strings.
      row[weekDay] = addDecimals(row[weekDay], hours);
      this.billingTotal = addDecimals(this.billingTotal, hours);
      row.total = addDecimals(row.total, hours);
    });
    return row;
  }

  private makeEmptyEquipmentBillingSummaryRow(billCode: string): IEquipmentBillingSummaryRow {
    return {
      equipment: this.display,
      total: 0,
      billCode: billCode ? billCode : CatsNoBillCodeDescriptionEnum.Unassigned,
      sun: 0,
      mon: 0,
      tue: 0,
      wed: 0,
      thu: 0,
      fri: 0,
      sat: 0,
    };
  }

  showBulkAdd() {
    let config;
    if (this.isPayroll || this.payrollGrid.length) config = this.getPayrollDetailPayneConfig();
    else config = this.getBillingDetailPayneConfig();
    this.datahub.detailPayneHandler.showBulkAdd(this, config);
  }

  getPayrollDetailPayneConfig(_day?: IWeekDayNumbers, record?: ICatRecord) {
    if (record) {
      const found = this.datahub.payrollTimesheet.extDocuments.find((x) => safeCompare(x.Documentno, record.Extdocumentno));
      if (found) return found.makeForeignConfig(makePayrollEquipmentDailyConfig());
    }
    return makePayrollEquipmentDailyConfig();
  }

  getBillingDetailPayneConfig(record?: ICatRecord) {
    if (record) {
      const found = this.datahub.billingTimesheet.extDocuments.find((x) => safeCompare(x.Documentno, record.Extdocumentno));
      if (found) return found.makeForeignConfig(makeBillingEquipmentDailyConfig());
    }
    return makeBillingEquipmentDailyConfig();
  }

  getPayrollDetailPayneValues(_row: number, column: ITableConfigEntity<IDayOfWeek>) {
    this.focusedDay = this.datahub.payrollTimesheet.getDate(column.key);
    this.detailPayneRecords = this.visibleCatsRecords
      .filter((record) => convertSapDateToWeekDayString(record.Workdate) === column.key)
      .map((x) => new EquipmentCatsRecord(this, this.getPayrollDetailPayneConfig(undefined, x)).fillStateFromSap(x));
  }

  launchPayrollDetailPayneHandler(row: number, column: ITableConfigEntity<IDayOfWeek>) {
    this.getPayrollDetailPayneValues(row, column);
    if (this.focusedDay != null && this.isInvalid(this.focusedDay.number)) {
      this.datahub.detailPayneHandler.showInvalidEntity(this, 'equipment');
    } else {
      this.datahub.detailPayneHandler.showPayrollEquipment(this);
    }
  }

  getBillingDetailPayneValues(row: number, column: ITableConfigEntity<IDayOfWeek>) {
    this.focusedDay = this.datahub.billingTimesheet.getDate(column.key);
    if (this.isInvalid(this.focusedDay.number)) {
      this.datahub.detailPayneHandler.showInvalidEntity(this, 'equipment');
      return;
    }
    let material = this.billingGrid[row]?.billCode ?? '';
    if (material === CatsNoBillCodeDescriptionEnum.Unassigned || material === CatsNoBillCodeDescriptionEnum.NoDefault) {
      material = '';
    }
    const records = this.visibleCatsRecords.filter((record) => material === record.Zmatnr);
    this.detailPayneRecords = records
      .filter((record) => convertSapDateToWeekDayString(record.Workdate) === column.key)
      .map((x) => new EquipmentCatsRecord(this, this.getBillingDetailPayneConfig(x)).fillStateFromSap(x));
    this.datahub.detailPayneHandler.showBillingEquipment(this);
  }

  getPlaceholderEmployee() {
    return this.datahub.user.getEmpId();
  }

  addNewRecord(config: ITableConfigEntity[]): EquipmentCatsRecord {
    const record = new EquipmentCatsRecord(this, config);
    return record.getRequiredObject(this);
  }

  handleMassBillCodeChange$(current: string, desired: string, timesheetNo: string) {
    let records = this.catsRecords.filter((x) => x.Zmatnr === current);
    if (timesheetNo) records = records.filter((x) => safeCompare(x.Extdocumentno, timesheetNo));
    const update = records.map((x) => {
      return {
        Zmatnr: desired,
        Counter: x.Counter,
      };
    });
    const object: IOutgoingCatsArrays = {
      create: [],
      delete: [],
      update: update,
      all: update,
    };
    return this.save$(object);
  }

  save$(records: IOutgoingCatsArrays): Observable<ICatRecord[]> {
    return this.datahub.catsDB.syncSingleEntityWithCats$(records).pipe(
      tap((catsRecords: ICatRecord[]) => {
        records.update
          .concat(records.delete)
          .forEach((record) => (this.catsRecords = this.catsRecords.filter((x) => x.Counter !== record.Counter))); //remove from the equipment each record that shares a counter with the incoming records.
        this.catsRecords = this.catsRecords.concat(catsRecords); //add the incoming records instead
        this.setCatsRecords(this.catsRecords); //re-render the grid
      }),
    );
  }

  setDefaultCode(contract: ContractHandler) {
    this.defaultBillCode = contract.getEquipmentDefault(this);
    if (!this.defaultBillCode) this.defaultBillCode = CatsNoBillCodeDescriptionEnum.NoDefault;
  }

  generateBillcodeDefaultInterface(custGrp: string, condGrp: string, billcode: string): IConditionToDefault {
    return {
      CustGroup: custGrp,
      CondGroup: condGrp,
      Pernr: '',
      EquipmentId: this.EquipmentId,
      BillCode: billcode,
      Activity: '',
    };
  }
}
